import React, {
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
} from 'antd';
import i18n from '../../config/i18n';
import connect from '../../toolbox/connectActionsAndReducers';
import { REGEX_PASSWORD } from '../../toolbox/regex';
import { INVITED, QUICK } from '../../constants/SignUpTypes';
import { DOCTOR_ID } from '../../constants/RoleConstants';

const { Option } = Select;

const styles = {
  container: {
    padding: '0px 15px',
  },
};


const SignUpContainer = ({
  token,
  email,
  router: {
    navigate,
  },
  actions: {
    quickSignup,
    dismissLastAlert,
    signUp,
  },
  metadata: {
    data: {
      title,
      specialty,
    },
  },
  form: {
    getFieldDecorator,
    getFieldValue,
    getFieldsValue,
    resetFields,
    validateFields,
    validateFieldsAndScroll,
  },
}) => {
  const [hasCheckedOnAgreement, setHasCheckedOnAgreement] = useState(false);
  const [titleOptions, setTitleOptions] = useState([]);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const signUpType = token && email ? INVITED : QUICK;

  const generateSelectOpt = data => (
    data.map(obj => (<Option key={obj.name} value={obj.id}>{obj.name}</Option>))
  );

  useEffect(() => {
    // clean up previous login states
    localStorage.clear();
  }, []);

  useEffect(() => {
    // Quick signup is for doctor now.
    setTitleOptions(generateSelectOpt(
      (title || []).filter(({ roleId }) => roleId === DOCTOR_ID)
    ));
  }, [title]);

  useEffect(() => {
    setSpecialtyOptions(generateSelectOpt(specialty || []));
  }, [specialty]);

  const onSubmitClick = () => {
    // validate all fields.
    validateFieldsAndScroll(
      { scroll: { offsetTop: 120 } },
      (errors) => { setIsModalVisible(!errors); }
    );
  };

  const onCancelClick = () => {
    setIsModalVisible(false);
  };

  const onConfirmClick = async () => {
    // submit the user data to API
    if (signUpType === QUICK) {
      if (await quickSignup(getFieldsValue())) {
        setTimeout(() => {
          // force refresh to make sure existing localStorage data won't interfere
          navigate('/login', { trigger: true });
          dismissLastAlert();
        }, 2000);
      }
    } else {
      const userData = {
        ...getFieldsValue([
          'email',
          'password',
          'firstName',
          'lastName',
        ]),
        token,
      };

      if (await signUp(userData)) {
        setTimeout(() => {
          // force refresh to make sure existing localStorage data won't interfere
          navigate('/login', { trigger: true });
          dismissLastAlert();
        }, 2000);
      }
    }

    setIsModalVisible(false);
  };

  const onPasswordChange = () => {
    // Empty the confirm password input to force user to type password again.
    resetFields(['confirmPassword']);
  };

  const validatePassword = (rule, value, callback) => {
    /**
     * Apply password rule
     *   1. At least one number, one lowercase and one uppercase letter
     *   2. At least seven characters
     */
    if (REGEX_PASSWORD.test(value)) {
      callback();
    } else {
      callback(i18n.t('pwdRules'));
    }

    // Should always check if passwords match on password input change.
    validateFields(['confirmPassword'], () => {});
  };

  const validateConfirmPassword = (rule, value, callback) => {
    if (value === getFieldValue('password')) {
      callback();
    } else {
      callback(i18n.t('pwdNotMatch'));
    }
  };

  const generateFormItem = data => data.map(({
    id,
    label,
    options,
    children,
  }) => (
    <Form.Item
      label={label}
      key={id}
    >
      {getFieldDecorator(id, options)(children)}
    </Form.Item>
  ));

  const fields = {
    [INVITED]: [
      {
        id: 'email',
        label: i18n.t('email'),
        options: {
          initialValue: email,
          rules: [{ required: true, message: ' ' }],
        },
        children: <Input disabled={signUpType === INVITED} />,
      },
      {
        id: 'password',
        label: i18n.t('pwd'),
        options: {
          initialValue: '',
          rules: [
            { required: true, message: ' ' },
            { validator: validatePassword },
          ],
        },
        children: (
          // Disable autofill.
          // https://developer.mozilla.org/en-US/docs/Web/Security/Securing_your_site/Turning_off_form_autocompletion
          <Input
            autoComplete="new-password"
            type="password"
            onChange={onPasswordChange}
          />
        ),
      },
      {
        id: 'confirmPassword',
        label: i18n.t('confirmPwd'),
        options: {
          initialValue: '',
          rules: [
            { required: true, message: ' ' },
            { validator: validateConfirmPassword },
          ],
        },
        children: <Input type="password" />,
      },
      {
        id: 'firstName',
        label: i18n.t('firstName'),
        options: {
          initialValue: '',
          rules: [{ required: true, message: ' ' }],
        },
        children: <Input />,
      },
      {
        id: 'lastName',
        label: i18n.t('lastName'),
        options: {
          initialValue: '',
          rules: [{ required: true, message: ' ' }],
        },
        children: <Input />,
      },
    ],
    [QUICK]: [
      {
        id: 'medicalGroup',
        label: i18n.t('signupMedicalGroup'),
        options: {
          initialValue: '',
          rules: [{ required: true, message: ' ' }],
        },
        children: <Input />,
      },
      {
        id: 'specialtyId',
        label: i18n.t('signupSpecialty'),
        options: {
          rules: [{ required: true, message: ' ' }],
        },
        children: (
          <Select
            id="specialtyId"
          >
            {specialtyOptions}
          </Select>
        ),
      },
      {
        id: 'titleId',
        label: i18n.t('signupTitle'),
        options: {
        },
        children: (
          <Select
            id="titleId"
          >
            {titleOptions}
          </Select>
        ),
      },
      {
        id: 'npi',
        label: i18n.t('signupNPI'),
        options: {
          initialValue: '',
          rules: [{ pattern: /^\d{10}$/, message: i18n.t('NPIRules') }],
        },
        children: <Input />,
      },
    ],
    other: [
      {
        id: 'agreementCheck',
        options: {
          initialValue: hasCheckedOnAgreement,
        },
        children: (
          <Checkbox
            onChange={() => { setHasCheckedOnAgreement(!hasCheckedOnAgreement); }}
            checked={hasCheckedOnAgreement}
          >
            {i18n.t('iAgreeTo')} <a href="/user-agreement" target="_blank">{i18n.t('userAgreement')}</a>
          </Checkbox>
        ),
      },
      {
        id: 'submit',
        options: {},
        children: (
          <Button
            type="primary"
            disabled={!hasCheckedOnAgreement}
            style={{ float: 'right' }}
            onClick={onSubmitClick}
          >
            {i18n.t('submit')}
          </Button>
        ),
      },
    ],
  };

  return (
    <div className="SignUpContainer" style={styles.container}>
      <h1>
        {i18n.t('createAccount')}
      </h1>
      <p className="lead">
        {
          signUpType === INVITED
            ? i18n.t('allFieldsRequired')
            : i18n.t('markedFieldsRequired')
        }
      </p>
      <Form>
        {generateFormItem(fields[INVITED])}
        {signUpType === QUICK && generateFormItem(fields[QUICK])}
        {generateFormItem(fields.other)}
      </Form>
      <p>
        {i18n.t('alreadyHaveAccount_qus')}&nbsp;
        <a href="/login">
          {i18n.t('login')}
        </a>
      </p>
      <Modal
        visible={isModalVisible}
        onOk={onConfirmClick}
        onCancel={onCancelClick}
        cancelText={i18n.t('cancel')}
      >
        {i18n.t('signUpMsg')}
      </Modal>
    </div>
  );
};

SignUpContainer.propTypes = {
  token: PropTypes.string,
  email: PropTypes.string,
  router: PropTypes.shape({
    navigate: PropTypes.func.isRequired,
  }).isRequired,
  actions: PropTypes.shape({
    quickSignup: PropTypes.func.isRequired,
    createSuccessAlert: PropTypes.func.isRequired,
    createDangerAlert: PropTypes.func.isRequired,
    dismissLastAlert: PropTypes.func.isRequired,
    signUp: PropTypes.func.isRequired,
  }).isRequired,
  form: PropTypes.shape({
    getFieldDecorator: PropTypes.func.isRequired,
    getFieldValue: PropTypes.func.isRequired,
    getFieldsValue: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
    validateFields: PropTypes.func.isRequired,
    validateFieldsAndScroll: PropTypes.func.isRequired,
  }).isRequired,
};

SignUpContainer.defaultProps = {
  token: '',
  email: '',
};

export default connect(Form.create()(SignUpContainer));



// WEBPACK FOOTER //
// ./src/containers/login/SignUpContainer.js